import createClient from 'openapi-fetch';

// src/client/clients/CordataBmdbSearchClient.ts

// src/client/querySerializer.ts
var isScalar = (item) => ["string", "number", "boolean"].includes(typeof item);
var serializeValue = (value) => {
  if (value === void 0) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.filter(isScalar).map((entry) => encodeURIComponent(entry)).join(",");
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return encodeURIComponent(value);
  }
  throw new Error(`BMDB client cannot serialize query string value '${JSON.stringify(value)}'`);
};
var serializeKeyValue = (key, value) => {
  const serializedValue = serializeValue(value);
  return serializedValue === "" ? void 0 : `${key}=${serializedValue}`;
};
var serializeParameter = ([key, value]) => (!!value && value.constructor === Object ? Object.entries(value).map(([objKey, objValue]) => serializeKeyValue(`${key}[${objKey}]`, objValue)) : [serializeKeyValue(key, value)]).filter((entry) => entry !== void 0);
var standardQuerySerializer = (params) => params && typeof params === "object" ? Object.entries(params).flatMap(serializeParameter).join("&") : "";

// src/client/errors.ts
var formatApiErrors = (errors) => errors.map(({ code, info }) => `${code}: ${info}`).join(", ");
var BmdbSearchClientRequestError = class extends Error {
  constructor(errors) {
    const message = `Invalid BMD Search request: ${formatApiErrors(errors)}`;
    super(message);
    this.errors = errors;
  }
};
var BmdbSearchClientServerError = class extends Error {
  constructor(errors) {
    const message = errors.length ? `BMD search failed due to server error: ${formatApiErrors(errors)}` : `BMD search failed due to an unknown error`;
    super(message);
    this.errors = errors;
  }
};
var isServerError = (error) => {
  switch (error.code) {
    case 500:
      return true;
    default:
      return false;
  }
};
var parseErrorResponse = (error) => {
  const { errors } = error;
  const serverErrors = errors.filter(isServerError);
  return !errors.length || serverErrors.length ? new BmdbSearchClientServerError(serverErrors) : new BmdbSearchClientRequestError(errors);
};

// src/client/processResponse.ts
var processResponse = ({ data, error }) => {
  if (error) {
    return { error: parseErrorResponse(error) };
  } else {
    return { data };
  }
};

// src/client/constants.ts
var globalCatalogueId = "global";
var defaultHost = "https://search.data.bs.no";

// src/client/version.ts
var version = "0.6.1";

// src/client/clients/CordataBmdbSearchClient.ts
var createCordataBmdbSearchClient = ({
  catalogueId = globalCatalogueId,
  host = defaultHost,
  clientIdentifier,
  featureFlags,
  fallbackCoverImages: fallback_cover_images
}) => {
  const api = createClient({
    baseUrl: new URL(`/cordata/${catalogueId}/api/`, host).toString(),
    querySerializer: standardQuerySerializer,
    headers: { "Client-Identifier": clientIdentifier }
  });
  const client = {
    getWorks: async (params) => {
      const response = await api.GET("/work", {
        params: { query: { ...params, ft: featureFlags, version, fallback_cover_images } }
      });
      return processResponse(response);
    },
    getAgents: async (params) => {
      const response = await api.GET("/agent", { params: { query: { ...params, version } } });
      return processResponse(response);
    },
    getAgentRoles: async (params) => {
      const response = await api.GET("/agent/roles", { params: { query: { ...params, version } } });
      return processResponse(response);
    },
    searchWorks: async (params) => {
      const response = await api.GET("/work/search", {
        params: { query: { ...params, ft: featureFlags, version, fallback_cover_images } }
      });
      return processResponse(response);
    },
    searchAgents: async (params) => {
      const response = await api.GET("/agent/search", { params: { query: { ...params, version } } });
      return processResponse(response);
    }
  };
  return client;
};
var createPlasBmdbSearchClient = ({
  catalogueId = globalCatalogueId,
  host = defaultHost,
  clientIdentifier,
  featureFlags,
  fallbackCoverImages: fallback_cover_images
}) => {
  const api = createClient({
    baseUrl: new URL(`/ol/${catalogueId}/api/`, host).toString(),
    querySerializer: standardQuerySerializer,
    headers: { "Client-Identifier": clientIdentifier }
  });
  const client = {
    getWorks: async (params) => {
      const response = await api.GET("/work", {
        params: { query: { ...params, ft: featureFlags, version, fallback_cover_images } }
      });
      return processResponse(response);
    },
    getAgents: async (params) => {
      const response = await api.GET("/agent", { params: { query: { ...params, version } } });
      return processResponse(response);
    },
    searchWorks: async (params) => {
      const response = await api.GET("/work/search", {
        params: { query: { ...params, ft: featureFlags, version, fallback_cover_images } }
      });
      return processResponse(response);
    },
    searchAgents: async (params) => {
      const response = await api.GET("/agent/search", { params: { query: { ...params, version } } });
      return processResponse(response);
    },
    getPublications: async (params) => {
      const response = await api.GET("/publication", { params: { query: { ...params, version } } });
      return processResponse(response);
    },
    getConfiguration: async (params) => {
      const response = await api.GET("/configuration/search", { params: { query: { ...params, version } } });
      return processResponse(response);
    }
  };
  return client;
};

// src/client/clients/DefaultBmdbSearchClient.ts
var createBmdbSearchClient = ({
  catalogueId = globalCatalogueId,
  host = defaultHost,
  clientIdentifier,
  featureFlags = [],
  fallbackCoverImages
}) => {
  const apis = {
    cordata: createCordataBmdbSearchClient({ catalogueId, host, clientIdentifier, featureFlags, fallbackCoverImages }),
    plas: createPlasBmdbSearchClient({ catalogueId, host, clientIdentifier, featureFlags, fallbackCoverImages })
  };
  const client = {
    searchWorks: apis.cordata.searchWorks,
    getWorks: apis.cordata.getWorks,
    searchAgents: apis.cordata.searchAgents,
    getAgents: apis.cordata.getAgents,
    getAgentRoles: apis.cordata.getAgentRoles,
    getPublications: apis.plas.getPublications,
    getConfiguration: apis.plas.getConfiguration
  };
  return client;
};

// src/client/swr/bmdbFetcher.ts
var bmdbFetcher = async ({
  operation,
  params,
  host,
  clientIdentifier,
  catalogueId,
  featureFlags,
  fallbackCoverImages
}) => {
  const client = createBmdbSearchClient({
    host,
    catalogueId,
    clientIdentifier,
    featureFlags,
    fallbackCoverImages
  });
  const { data, error } = await client[operation](params);
  if (error) {
    throw error;
  }
  return data;
};

// src/client/swr/bmdbWithContextFetcher.ts
var bmdbWithContextFetcher = async ({
  apiContext,
  operation,
  params,
  host,
  clientIdentifier,
  catalogueId,
  featureFlags,
  fallbackCoverImages
}) => {
  const clientProps = {
    host,
    catalogueId,
    clientIdentifier,
    featureFlags,
    fallbackCoverImages
  };
  const client = {
    default: createBmdbSearchClient,
    cordata: createCordataBmdbSearchClient,
    plas: createPlasBmdbSearchClient
  }[apiContext](clientProps);
  const fn = client[operation];
  const { data, error } = await fn(params);
  if (error) throw error;
  return data;
};

// src/client/cordataWemTriples.ts
var getCordataWemTriples = (work) => (work.expressions ?? []).flatMap((expression) => [
  ...expression.manifestations?.map((manifestation) => ({
    work,
    expression,
    manifestation,
    isAggregateManifestation: false
  })) ?? [],
  ...expression.aggregateManifestations?.map((manifestation) => ({
    work,
    expression,
    manifestation,
    isAggregateManifestation: true
  })) ?? []
]);
var getRepresentativeCordataWemTriplesFromSearchResponse = (searchResponse) => searchResponse.results.map(
  ({ work, representativeManifestationId }) => getCordataWemTriples(work).find(({ manifestation }) => manifestation.id === representativeManifestationId)
).filter((triple) => !!triple);

// src/configuration/searchDefinition.ts
var createSearchDefinition = (props) => ({
  ...props,
  get facetNames() {
    return props.facets.map(({ name }) => name);
  },
  get sortNames() {
    return props.sorts.map(({ name }) => name);
  },
  get filterNames() {
    return props.filters.map(({ name }) => name);
  },
  getFacet(facetName) {
    return props.facets.find(({ name }) => name === facetName);
  },
  getFilter(filterName) {
    return props.filters.find(({ name }) => name === filterName);
  }
});

// src/configuration/agentSearchDefinition.ts
var sorts = [
  {
    name: "plas.relevance.desc",
    description: "Relevance"
  }
];
var filters = [
  {
    name: "bmdb.nationality",
    description: "TODO"
  },
  {
    name: "bmdb.bibbiAuthorityId",
    description: "TODO"
  },
  {
    name: "bmdb.libraryCatalogue",
    description: "TODO"
  }
];
var facets = [
  {
    name: "bmdb.nationality",
    description: "TODO",
    label: {
      nb: "Nasjonalitet",
      nn: "Nasjonalitet"
    }
  }
];
var agentSearchDefinition = createSearchDefinition({
  sorts,
  filters,
  facets,
  defaultSort: "plas.relevance.desc"
});

// src/configuration/manifestationSearchDefinition.ts
var sorts2 = [
  {
    name: "plas.relevance.desc",
    description: "Relevance"
  }
];
var filters2 = [
  // PLAS API filters
  {
    name: "ola.mediaType",
    description: "TODO"
  },
  // Unstable filters (sorted alphabetically)
  {
    name: "bmdb.libraryCatalogue",
    description: "TODO"
  },
  {
    name: "bmdb.hasCover",
    description: "Whether the manifestation has a cover image or not"
  }
];
var facets2 = [
  // PLAS API facets (stable)
  {
    name: "ola.mediaType",
    description: "TODO",
    label: {
      nb: "Medietype",
      nn: "Medietype"
    }
  },
  // BS facets (stable)
  //
  // (none yet)
  //
  // Unstable facets
  {
    name: "bmdb.libraryCatalogue",
    description: "TODO",
    label: {
      nb: "Bibliotekskatalog",
      nn: "Bibliotekskatalog"
    }
  }
];
var manifestationSearchConfig = createSearchDefinition({
  sorts: sorts2,
  filters: filters2,
  facets: facets2,
  defaultSort: "plas.relevance.desc"
});

// src/configuration/productSearchDefinition.ts
var sorts3 = [
  {
    name: "relevance.desc",
    description: "Relevance (descending)"
  },
  {
    name: "updateTime.desc",
    description: "Time when the work was last updated in the search index (descending)."
  }
];
var filters3 = [
  {
    name: "id",
    description: "ID"
  },
  {
    name: "source",
    description: "Source"
  },
  // Fellesfiltre
  {
    name: "produkt.kategori",
    description: "Overordnet produktkategori i bokhandelen"
  },
  {
    name: "produkt.utgivelsesdato",
    description: "Utgivelsesdato"
  },
  // Filtre for bøker
  {
    name: "bok.spr\xE5k",
    description: "B\xF8ker: Spr\xE5k"
  },
  {
    name: "bok.m\xE5lgruppe",
    description: "B\xF8ker: M\xE5lgruppe"
  },
  {
    name: "bok.litteraturtype",
    description: "B\xF8ker: Litteraturtype"
  },
  {
    name: "bok.sjanger",
    description: "B\xF8ker: Sjanger"
  },
  {
    name: "bok.fagtema",
    description: "B\xF8ker: Fagtema"
  },
  {
    name: "bok.alder",
    description: "B\xF8ker: Alder (anbefalt m\xE5lgruppe, ikke aldersgrense)"
  },
  {
    name: "bok.innbinding",
    description: "B\xF8ker: Innbinding"
  },
  {
    name: "bok.form",
    description: "B\xF8ker: Form"
  },
  {
    name: "bok.tilrettelegging",
    description: "B\xF8ker: Tilrettelegging"
  },
  {
    name: "bok.kulturfond",
    description: "B\xF8ker: Kulturfond"
  },
  // Filtre for film
  {
    name: "film.originalspr\xE5k",
    description: "Film: Originalspr\xE5k?"
  },
  {
    name: "film.nasjonalitet",
    description: "Film: Nasjonalitet"
  },
  {
    name: "film.m\xE5lgruppe",
    description: "Film: M\xE5lgruppe"
  },
  {
    name: "film.aldersgrense",
    description: "Film: Aldersgrense"
  },
  {
    name: "film.sjanger",
    description: "Film: Sjanger"
  },
  {
    name: "film.format",
    description: "Film: Format (DVD, Blu-Ray osv.)"
  },
  // Filtre for dataspill
  {
    name: "spill.m\xE5lgruppe",
    description: "Spill: M\xE5lgruppe"
  },
  {
    name: "spill.aldersgrense",
    description: "Spill: Aldersgrense"
  },
  {
    name: "spill.konsoll",
    description: "Spill: Konsoll"
  },
  {
    name: "spill.sjanger",
    description: "Spill: Sjanger"
  }
];
var facets3 = [
  {
    name: "source",
    description: "Source",
    label: {
      nb: "Datakilde",
      nn: "Datakilde"
    }
  },
  // Fellesfasetter
  {
    name: "produkt.kategori",
    description: "Overordnet produktkategori i bokhandelen",
    label: {
      nb: "Kategori",
      nn: "Kategori"
    }
  },
  {
    name: "produkt.utgivelsesdato",
    description: "Utgivelsesdato",
    label: {
      nb: "Utgivelsesdato",
      nn: "Utgivelsesdato"
    }
  },
  // Fasetter for bøker
  {
    name: "bok.spr\xE5k",
    description: "B\xF8ker: Spr\xE5k",
    label: {
      nb: "Spr\xE5k",
      nn: "Spr\xE5k"
    }
  },
  {
    name: "bok.m\xE5lgruppe",
    description: "B\xF8ker: M\xE5lgruppe",
    label: {
      nb: "M\xE5lgruppe",
      nn: "M\xE5lgruppe"
    }
  },
  {
    name: "bok.litteraturtype",
    description: "B\xF8ker: Litteraturtype",
    label: {
      nb: "Litteraturtype",
      nn: "Litteraturtype"
    }
  },
  {
    name: "bok.sjanger",
    description: "B\xF8ker: Sjanger",
    label: {
      nb: "Sjanger",
      nn: "Sjanger"
    }
  },
  {
    name: "bok.fagtema",
    description: "B\xF8ker: Fagtema",
    label: {
      nb: "Fagtema",
      nn: "Fagtema"
    }
  },
  {
    name: "bok.alder",
    description: "B\xF8ker: Alder (anbefalt m\xE5lgruppe, ikke aldersgrense)",
    label: {
      nb: "Alder (anbefalt m\xE5lgruppe, ikke aldersgrense)",
      nn: "Alder (anbefalt m\xE5lgruppe, ikke aldersgrense)"
    }
  },
  {
    name: "bok.innbinding",
    description: "B\xF8ker: Innbinding",
    label: {
      nb: "Innbinding",
      nn: "Innbinding"
    }
  },
  {
    name: "bok.form",
    description: "B\xF8ker: Form",
    label: {
      nb: "Form",
      nn: "Form"
    }
  },
  {
    name: "bok.tilrettelegging",
    description: "B\xF8ker: Tilrettelegging",
    label: {
      nb: "Tilrettelegging",
      nn: "Tilrettelegging"
    }
  },
  {
    name: "bok.kulturfond",
    description: "B\xF8ker: Kulturfond",
    label: {
      nb: "Kulturfond",
      nn: "Kulturfond"
    }
  },
  // Fasetter for film
  {
    name: "film.originalspr\xE5k",
    description: "Film: Originalspr\xE5k?",
    label: {
      nb: "Originalspr\xE5k",
      nn: "Originalspr\xE5k"
    }
  },
  {
    name: "film.nasjonalitet",
    description: "Film: Nasjonalitet",
    label: {
      nb: "Nasjonalitet",
      nn: "Nasjonalitet"
    }
  },
  {
    name: "film.m\xE5lgruppe",
    description: "Film: M\xE5lgruppe",
    label: {
      nb: "M\xE5lgruppe",
      nn: "M\xE5lgruppe"
    }
  },
  {
    name: "film.aldersgrense",
    description: "Film: Aldersgrense",
    label: {
      nb: "Aldersgrense",
      nn: "Aldersgrense"
    }
  },
  {
    name: "film.sjanger",
    description: "Film: Sjanger",
    label: {
      nb: "Sjanger",
      nn: "Sjanger"
    }
  },
  {
    name: "film.format",
    description: "Film: Format (DVD, Blu-Ray osv.)",
    label: {
      nb: "Format (DVD, Blu-Ray osv.)",
      nn: "Format (DVD, Blu-Ray osv.)"
    }
  },
  // Fasetter for dataspill
  {
    name: "spill.m\xE5lgruppe",
    description: "Spill: M\xE5lgruppe",
    label: {
      nb: "M\xE5lgruppe",
      nn: "M\xE5lgruppe"
    }
  },
  {
    name: "spill.aldersgrense",
    description: "Spill: Aldersgrense",
    label: {
      nb: "Aldersgrense",
      nn: "Aldersgrense"
    }
  },
  {
    name: "spill.konsoll",
    description: "Spill: Konsoll",
    label: {
      nb: "Konsoll",
      nn: "Konsoll"
    }
  },
  {
    name: "spill.sjanger",
    description: "Spill: Sjanger",
    label: {
      nb: "Sjanger",
      nn: "Sjanger"
    }
  }
];
var productSearchDefinition = createSearchDefinition({
  sorts: sorts3,
  filters: filters3,
  facets: facets3,
  defaultSort: "relevance.desc"
});

// src/configuration/workSearchDefinition.ts
var sorts4 = [
  {
    name: "plas.relevance.desc",
    description: "Relevance (descending)"
  },
  {
    name: "plas.relevance.asc",
    description: "Relevance (ascending)"
  },
  {
    name: "plas.publicationDate.desc",
    description: "Time when a publication of the work was published (descending)."
  },
  {
    name: "plas.publicationDate.asc",
    description: "Time when a publication of the work was published (ascending)."
  },
  {
    name: "bmdb.firstPublicationDate.desc",
    description: "Time when the first catalogued publication of the work was published or created (descending). Note that older, uncatalogued publications may exist."
  },
  {
    name: "bmdb.firstPublicationDate.asc",
    description: "Time when the first catalogued publication of the work was published or created (ascending). Note that older, uncatalogued publications may exist."
  },
  {
    name: "bmdb.updateTime.desc",
    description: "Time when the work was last updated in the search index (descending)."
  },
  {
    description: "Time when a publication of the work was catalogued (descending).",
    name: "bmdb.promusCataloguingTime.desc"
  },
  {
    name: "bmdb.numberOfCatalogueRecords.desc",
    description: "Number of catalogue records (descending)"
  },
  {
    name: "bmdb.numberOfCatalogueRecords.asc",
    description: "Number of catalogue records (ascending)"
  }
];
var filters4 = [
  // PLAS API filters
  {
    name: "plas.mediaTypes",
    description: "TODO"
  },
  {
    name: "plas.subjects",
    description: "TODO"
  },
  {
    name: "plas.genres",
    description: "TODO"
  },
  {
    name: "plas.forms",
    description: "TODO"
  },
  {
    name: "plas.language",
    description: "TODO"
  },
  {
    name: "plas.audience.ageGroup",
    description: "TODO"
  },
  {
    name: "plas.audience.ageRange",
    description: "TODO"
  },
  {
    name: "plas.agent",
    description: "TODO"
  },
  // BMDB filters
  {
    name: "bmdb.contributor",
    description: "TODO"
  },
  {
    name: "bmdb.bibbiAuthorityId",
    description: "TODO"
  },
  {
    name: "bmdb.libraryCatalogue",
    deprecated: true,
    description: "TODO"
  },
  {
    name: "bmdb.catalogue",
    description: "Only return works with a manifestation linked to a record from the given external catalogues. Examples: 'sandnes' (library catalogue), 'filmoteket' (content service catalogue)"
  },
  {
    name: "bmdb.catalogueRecord",
    description: "Only return works with a manifestation linked to the given external catalogue records. Format: '{catalogueId}:{recordId}'. Examples: 'sandnes:239941', 'filmoteket:195649'"
  },
  {
    name: "bmdb.originalLanguage",
    description: "TODO"
  },
  {
    name: "bmdb.originalYear",
    description: "TODO"
  },
  {
    name: "bmdb.publicationDate",
    description: "TODO"
  },
  {
    name: "bmdb.series",
    description: "TODO"
  },
  {
    name: "bmdb.seriesWork",
    description: "TODO"
  },
  {
    name: "bmdb.workType",
    description: "TODO"
  },
  {
    name: "bmdb.nationality",
    description: "TODO"
  },
  {
    name: "bmdb.isFiction",
    description: "TODO"
  },
  {
    name: "bmdb.warningCode",
    description: "TODO"
  },
  {
    name: "bmdb.format",
    description: "TODO"
  },
  {
    name: "bmdb.medium",
    description: "TODO"
  },
  {
    name: "bmdb.isShortWork",
    description: "Whether the work is a short work (novel, audio track, etc.). Valid values are 'true' and 'false'."
  },
  {
    name: "bmdb.appealTerm",
    description: "TODO"
  },
  {
    name: "bmdb.workOrigin",
    description: "TODO"
  },
  {
    name: "bmdb.manifestationOrigin",
    description: "TODO"
  },
  {
    name: "bmdb.instrumentation",
    description: "TODO"
  },
  {
    name: "bmdb.encodingLevel",
    description: "TODO"
  },
  {
    name: "bmdb.accessibleContentFeature",
    description: "Whether the work has accessible content (easy to read)"
  },
  {
    name: "bmdb.gtin",
    description: "ISBN-13 or EAN"
  }
];
var facets4 = [
  // PLAS API facets (stable)
  {
    name: "plas.mediaTypes",
    description: "TODO",
    label: {
      nb: "Medietype",
      nn: "Medietype"
    }
  },
  {
    name: "plas.subjects",
    description: "TODO",
    label: {
      nb: "Emne",
      nn: "Emne"
    }
  },
  {
    name: "plas.genres",
    description: "TODO",
    label: {
      nb: "Sjanger",
      nn: "Sjanger"
    }
  },
  {
    name: "plas.forms",
    description: "TODO",
    label: {
      nb: "Form",
      nn: "Form"
    }
  },
  {
    name: "plas.audience.ageRange",
    description: "TODO",
    label: {
      nb: "M\xE5lgruppe: Aldersspenn",
      nn: "M\xE5lgruppe: Aldersspenn"
    }
  },
  {
    name: "plas.audience.ageGroup",
    description: "TODO",
    label: {
      nb: "M\xE5lgruppe: Aldersgruppe",
      nn: "M\xE5lgruppe: Aldersgruppe"
    }
  },
  {
    name: "plas.language",
    description: "TODO",
    label: {
      nb: "Spr\xE5k",
      nn: "Spr\xE5k"
    }
  },
  {
    name: "plas.agent",
    description: "TODO",
    label: {
      nb: "Bidragsyter (PLAS)",
      nn: "Bidragsytar (PLAS)"
    }
  },
  // BMDB facets
  {
    name: "bmdb.contributor",
    description: "TODO",
    label: {
      nb: "Bidragsyter",
      nn: "Bidragsytar"
    }
  },
  {
    name: "bmdb.libraryCatalogue",
    description: "TODO",
    deprecated: true,
    label: {
      nb: "Bibliotekskatalog (deprecated)",
      nn: "Bibliotekskatalog (deprecated)"
    }
  },
  {
    name: "bmdb.catalogue",
    deprecated: true,
    description: "TODO",
    label: {
      nb: "Bibliotekskatalog",
      nn: "Bibliotekskatalog"
    }
  },
  {
    name: "bmdb.originalLanguage",
    description: "TODO",
    label: {
      nb: "Originalspr\xE5k",
      nn: "Originalspr\xE5k"
    }
  },
  {
    name: "bmdb.series",
    description: "TODO",
    label: {
      nb: "Serieuttrykk",
      nn: "Serieuttrykk"
    }
  },
  {
    name: "bmdb.seriesWork",
    description: "TODO",
    label: {
      nb: "Serieverk",
      nn: "Serieverk"
    }
  },
  {
    name: "bmdb.workType",
    description: "TODO",
    label: {
      nb: "Verkstype",
      nn: "Verkstype"
    }
  },
  {
    name: "bmdb.isFiction",
    description: "TODO",
    label: {
      nb: "Skj\xF8nn/fag",
      nn: "Skj\xF8nn/fag"
    }
  },
  {
    name: "bmdb.format",
    description: "TODO",
    label: {
      nb: "Format",
      nn: "Format"
    }
  },
  {
    name: "bmdb.medium",
    description: "TODO",
    label: {
      nb: "Medium",
      nn: "Medium"
    }
  },
  {
    name: "bmdb.warningCode",
    description: "TODO",
    label: {
      nb: "Konverteringsadvarsel",
      nn: "Konverterings\xE5tvaring"
    }
  },
  {
    name: "bmdb.appealTerm",
    description: "Appeal term from Forrigebok (beta)",
    label: {
      nb: "Appellterm",
      nn: "Appellterm"
    }
  },
  {
    name: "bmdb.workOrigin",
    description: "Original source for Cordata work",
    label: {
      nb: "Opprinnelig kilde for verk",
      nn: "Opprinneleg kjelde for verk"
    }
  },
  {
    name: "bmdb.manifestationOrigin",
    description: "Original source for Cordata manifestation",
    label: {
      nb: "Opprinnelig kilde for manifestasjon",
      nn: "Opprinneleg kjelde for manifestasjon"
    }
  },
  {
    name: "bmdb.instrumentation",
    description: "Instrumentation",
    label: {
      nb: "Instrumentering",
      nn: "Instrumentering"
    }
  },
  {
    name: "bmdb.encodingLevel",
    description: "Bibliographic encoding level",
    label: {
      nb: "Beskrivelsesniv\xE5",
      nn: "Beskrivingsniv\xE5"
    }
  },
  {
    name: "bmdb.accessibleContentFeature",
    description: "Whether the work has accessible content (easy to read)",
    label: {
      nb: "Tilgjengelig innhold",
      nn: "Tilgjengelig innhald"
    }
  }
];
var workSearchDefinition = createSearchDefinition({
  sorts: sorts4,
  filters: filters4,
  facets: facets4,
  defaultSort: "plas.relevance.desc"
});

// src/configuration/plasVersion.ts
var plasVersions = ["0.6.1"];
var defaultPlasVersion = "0.6.1";
var recommendedPlasVersion = "0.6.1";

// src/plas/mediaTypes.ts
var plasMediaTypes = [
  // Media types currently part of PLAS
  "book",
  "audiobook",
  "eBook",
  // Additional media types
  "artifact",
  "atlas",
  "languageCourse",
  "mixed",
  "music",
  "movie",
  "other",
  "periodical",
  "sheetMusic",
  "videogame",
  "website",
  "map"
];
var cordataFormatToPlasMediaType = {
  "Tredimensjonal gjenstand": "artifact",
  "Atlas": "atlas",
  "Lydbok": "audiobook",
  "E-lydbok": "audiobook",
  "Bok": "book",
  "Kart": "book",
  "E-bok": "eBook",
  "Spr\xE5kkurs": "languageCourse",
  "Kombidokument": "mixed",
  "Film": "movie",
  "Musikk": "music",
  "CD-ROM": "other",
  "Ark": "other",
  // TODO
  "Periodikum": "periodical",
  "Musikktrykk": "sheetMusic",
  "Dataspill": "videogame",
  "Nettressurs": "website"
};
var plasMediaTypeNames = {
  artifact: { nb: "Gjenstand", nn: "Gjenstand" },
  atlas: { nb: "Atlas", nn: "Atlas" },
  audiobook: { nb: "Lydbok", nn: "Lydbok" },
  book: { nb: "Bok", nn: "Bok" },
  eBook: { nb: "E-bok", nn: "E-bok" },
  languageCourse: { nb: "Spr\xE5kkurs", nn: "Spr\xE5kkurs" },
  map: { nb: "Kart", nn: "Kart" },
  mixed: { nb: "Kombinert", nn: "Kombinert" },
  movie: { nb: "Film", nn: "Film" },
  music: { nb: "Musikk", nn: "Musikk" },
  sheetMusic: { nb: "Noter", nn: "Notar" },
  other: { nb: "Annet", nn: "Anna" },
  periodical: { nb: "Tidsskrift", nn: "Tidsskrift" },
  videogame: { nb: "Dataspill", nn: "Dataspel" },
  website: { nb: "Nettside", nn: "Nettside" }
};

export { agentSearchDefinition, bmdbFetcher, bmdbWithContextFetcher, cordataFormatToPlasMediaType, createBmdbSearchClient, createCordataBmdbSearchClient, createPlasBmdbSearchClient, defaultPlasVersion, getCordataWemTriples, getRepresentativeCordataWemTriplesFromSearchResponse, manifestationSearchConfig, plasMediaTypeNames, plasMediaTypes, plasVersions, productSearchDefinition, recommendedPlasVersion, workSearchDefinition };
